/**
 * GMA-1629 (CompassCare Web 14/14 — Audit Log + Settings) — page composition.
 *
 * The final assembly screen of the facelift: an accessible two-tab layout
 * serving BOTH sidebar entries — the Audit Log (an append-only, filterable
 * event trail over the shared DataTable, filtered exclusively through the pure
 * model's filterAuditEntries) and Settings (the alert-threshold section driven
 * by the model's resolveThreshold override rules). Selecting an audit row
 * opens the shared DetailDrawer with the event detail body.
 *
 * Clinical-safety bar (non-negotiable):
 *  - the threshold override UI always shows the CURRENT value and the AGENCY
 *    DEFAULT side-by-side — a caregiver can never mistake an override for the
 *    default;
 *  - every threshold change routes through the model's validation
 *    (resolveThreshold) — a raw unvalidated number can never be committed;
 *    invalid input gets an explicit inline error and changes nothing;
 *  - any non-default threshold carries an explicit "Overridden" indicator
 *    (text, never color-only);
 *  - audit rows render ONLY the fields the model exposes (actor, event type,
 *    client target, severity, timestamp) — never PHI beyond that surface;
 *  - all colors come from the single design-token set; missing fields render
 *    a fail-safe "—"; null/undefined/malformed rows can never break the page.
 */
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import DataTable from "../../components/web/DataTable";
import SeverityBadge from "../../components/web/SeverityBadge";
import DetailDrawer from "../../components/web/DetailDrawer";
import {
  filterAuditEntries,
  resolveThreshold,
  AUDIT_EVENT_TYPES,
} from "./auditSettingsModel";
import {
  PLACEHOLDER,
  toEpochMs,
  formatMeasurement,
  formatRelativeTime,
} from "../../utils/webFormat";
import { theme, severityToken } from "../../styles/designTokens";

// Human labels for the model's frozen event-type vocabulary. Unknown types
// (a newer server) fall back to the raw type string — still model surface.
const EVENT_TYPE_LABELS = Object.freeze({
  alert_lifecycle: "Alert lifecycle",
  contact_attempt: "Contact attempt",
  sitrep: "SitRep",
  access_instruction_view: "Access instructions viewed",
  broadcast_send: "Broadcast sent",
  config_change: "Configuration change",
  report_export: "Report exported",
  permission_change: "Permission change",
});

// Audit severities offered as filter options (clinical escalation set — the
// informational "nora" level is not an audit severity).
const AUDIT_SEVERITIES = Object.freeze(["normal", "caution", "atRisk", "critical"]);

const TABS = Object.freeze([
  Object.freeze({ key: "audit", label: "Audit Log" }),
  Object.freeze({ key: "settings", label: "Settings" }),
]);

const COLUMNS = Object.freeze([
  { key: "actor", label: "Actor" },
  { key: "action", label: "Action" },
  { key: "target", label: "Target" },
  { key: "severity", label: "Severity" },
  { key: "when", label: "When", align: "right", mono: true },
  { key: "at", label: "Timestamp", align: "right", mono: true },
]);

const eventTypeLabel = (eventType) =>
  EVENT_TYPE_LABELS[eventType] || eventType || PLACEHOLDER;

// Target column: the client the event acted on. Agency-level events (no
// client) degrade to the placeholder — never a blank cell.
const targetLabel = (entry) => {
  if (entry.clientName) return entry.clientName;
  if (entry.clientId !== null && entry.clientId !== undefined) {
    return `Client ${entry.clientId}`;
  }
  return PLACEHOLDER;
};

// Absolute timestamp for the audit trail (the relative cell answers "how long
// ago", this one answers "exactly when"). Fail-safe: unparseable -> "—".
const formatAbsoluteTime = (value) => {
  const t = toEpochMs(value);
  if (t === null) return PLACEHOLDER;
  return new Date(t).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

// One labelled select in the audit filter bar.
function FilterSelect({ id, label, value, onChange, children }) {
  return (
    <label
      htmlFor={id}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        fontSize: 13,
        color: theme.textMuted,
      }}
    >
      {label}
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        style={{
          background: theme.surface,
          color: theme.textPrimary,
          border: `1px solid ${theme.border}`,
          borderRadius: 4,
          padding: "6px 8px",
          fontFamily: theme.fontUI,
          fontSize: 13,
        }}
      >
        {children}
      </select>
    </label>
  );
}

FilterSelect.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node,
};

// One labelled field in the audit detail drawer.
function DrawerField({ label, children }) {
  return (
    <section aria-label={label}>
      <h3
        style={{
          margin: "0 0 4px",
          fontSize: 13,
          color: theme.textMuted,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </h3>
      <div style={{ margin: 0, fontSize: 14 }}>{children}</div>
    </section>
  );
}

DrawerField.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

/**
 * One alert-threshold row. CURRENT vs AGENCY DEFAULT are always rendered
 * side-by-side; edits commit only through `onApply` (which routes through the
 * model), and invalid input surfaces an inline role="alert" error without
 * changing anything.
 */
function ThresholdRow({ threshold, resolved, onApply, onReset }) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState(null);
  const { key, label, unit } = threshold;

  const commit = () => {
    const trimmed = String(draft).trim();
    const parsed = trimmed === "" ? NaN : Number(trimmed);
    if (!Number.isFinite(parsed)) {
      // Reject at the boundary: no state change, explicit error.
      setError("Enter a valid number");
      return;
    }
    setError(null);
    setDraft("");
    onApply(key, parsed);
  };

  return (
    <div
      data-testid={`threshold-${key}`}
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 16,
        padding: "12px 16px",
        border: `1px solid ${theme.border}`,
        background: theme.surface,
      }}
    >
      <div style={{ flex: "1 1 180px", minWidth: 140, fontSize: 14 }}>
        {label}
      </div>
      <div style={{ display: "flex", gap: 24 }}>
        <div>
          <div style={{ fontSize: 12, color: theme.textMuted }}>
            Agency default
          </div>
          <div
            data-testid="threshold-default"
            style={{ fontFamily: theme.fontMono, fontSize: 14 }}
          >
            {formatMeasurement(resolved.agencyDefault, unit)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: theme.textMuted }}>Current</div>
          <div
            data-testid="threshold-current"
            style={{ fontFamily: theme.fontMono, fontSize: 14 }}
          >
            {formatMeasurement(resolved.value, unit)}
          </div>
        </div>
      </div>
      <div style={{ minWidth: 96 }}>
        {resolved.isOverride ? (
          <span data-testid="threshold-override">
            <SeverityBadge level="caution" label="Overridden" />
          </span>
        ) : (
          <span style={{ fontSize: 12, color: theme.textMuted }}>Default</span>
        )}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <input
          type="text"
          inputMode="decimal"
          aria-label={`New value for ${label}`}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") commit();
          }}
          style={{
            width: 90,
            background: theme.bg,
            color: theme.textPrimary,
            border: `1px solid ${theme.border}`,
            borderRadius: 4,
            padding: "6px 8px",
            fontFamily: theme.fontMono,
            fontSize: 13,
          }}
        />
        <button
          type="button"
          aria-label={`Apply ${label}`}
          onClick={commit}
          style={{
            background: "transparent",
            color: theme.textPrimary,
            border: `1px solid ${theme.border}`,
            borderRadius: 4,
            padding: "6px 12px",
            font: "inherit",
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          Apply
        </button>
        {resolved.isOverride ? (
          <button
            type="button"
            aria-label={`Reset ${label} to agency default`}
            onClick={() => {
              setError(null);
              setDraft("");
              onReset(key);
            }}
            style={{
              background: "transparent",
              color: theme.textMuted,
              border: `1px solid ${theme.border}`,
              borderRadius: 4,
              padding: "6px 12px",
              font: "inherit",
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Reset to default
          </button>
        ) : null}
        {error ? (
          <span
            role="alert"
            style={{ fontSize: 13, color: severityToken("critical").color }}
          >
            {error}
          </span>
        ) : null}
      </div>
    </div>
  );
}

ThresholdRow.propTypes = {
  threshold: PropTypes.shape({
    key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string,
    unit: PropTypes.string,
  }).isRequired,
  /** resolveThreshold output: { value, isOverride, agencyDefault }. */
  resolved: PropTypes.shape({
    value: PropTypes.number,
    isOverride: PropTypes.bool,
    agencyDefault: PropTypes.number,
  }).isRequired,
  onApply: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

// Real threshold rows only: an object with a usable key. Malformed config can
// never break the Settings render.
const validThresholds = (thresholds) =>
  (Array.isArray(thresholds) ? thresholds : []).filter(
    (t) =>
      t &&
      typeof t === "object" &&
      !Array.isArray(t) &&
      (typeof t.key === "string" || typeof t.key === "number") &&
      t.key !== "",
  );

export default function AuditSettingsPage({
  entries = [],
  thresholds = [],
  now,
  initialTab = "audit",
}) {
  const [tab, setTab] = useState(initialTab === "settings" ? "settings" : "audit");
  const [filters, setFilters] = useState({ actor: "", eventType: "", severity: "" });
  const [selected, setSelected] = useState(null); // audit display row | null
  // Committed threshold edits, keyed by threshold key. `null` = explicit
  // reset-to-default. Values only land here AFTER model validation.
  const [edits, setEdits] = useState({});

  const list = Array.isArray(entries) ? entries : [];

  // The audit trail through the pure model — the single filter authority.
  // filterAuditEntries also drops malformed rows, so the table can never
  // disagree with the model about what counts as an audit event.
  const visible = useMemo(
    () => filterAuditEntries(list, filters),
    [list, filters],
  );

  // Actor filter options come from the (unfiltered) valid trail.
  const actors = useMemo(() => {
    const all = filterAuditEntries(list, {})
      .map((e) => e.actor)
      .filter((a) => typeof a === "string" && a !== "");
    return [...new Set(all)].sort();
  }, [list]);

  const rows = useMemo(
    () =>
      visible.map((e, index) => ({
        id: e.id !== null && e.id !== undefined ? e.id : `event-${index}`,
        actor: e.actor || PLACEHOLDER,
        action: eventTypeLabel(e.eventType),
        target: targetLabel(e),
        severity: (
          <SeverityBadge
            level={e.severity}
            label={severityToken(e.severity).label}
          />
        ),
        when: formatRelativeTime(e.timestamp, now) || PLACEHOLDER,
        at: (
          <span data-testid="audit-absolute-time">
            {formatAbsoluteTime(e.timestamp)}
          </span>
        ),
        _severity: e.severity,
        _entry: e,
      })),
    [visible, now],
  );

  const thresholdList = useMemo(() => validThresholds(thresholds), [thresholds]);

  const setFilter = (name) => (value) =>
    setFilters((prev) => ({ ...prev, [name]: value }));

  const applyThreshold = (key, parsed) =>
    setEdits((prev) => ({
      ...prev,
      // Store the MODEL-resolved value, never the raw input: resolveThreshold
      // is the single override/validation authority (a non-finite value can
      // never get this far — the row rejects it before calling up).
      [key]: resolveThreshold(
        parsed,
        (thresholdList.find((t) => t.key === key) || {}).agencyDefault,
      ).value,
    }));

  const resetThreshold = (key) =>
    setEdits((prev) => ({ ...prev, [key]: null }));

  const tabStyle = (active) => ({
    padding: "8px 16px",
    font: "inherit",
    fontSize: 14,
    cursor: "pointer",
    background: "transparent",
    color: active ? theme.textPrimary : theme.textMuted,
    border: "none",
    borderBottom: `2px solid ${active ? theme.textPrimary : "transparent"}`,
  });

  return (
    <div
      data-testid="audit-settings-page"
      style={{
        display: "grid",
        gap: 16,
        padding: 16,
        background: theme.bg,
        color: theme.textPrimary,
        fontFamily: theme.fontUI,
      }}
    >
      <div
        role="tablist"
        aria-label="Audit and settings"
        style={{ display: "flex", gap: 8, borderBottom: `1px solid ${theme.border}` }}
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={`tab-${t.key}`}
            aria-selected={tab === t.key ? "true" : "false"}
            aria-controls={`panel-${t.key}`}
            tabIndex={tab === t.key ? 0 : -1}
            onClick={() => setTab(t.key)}
            onKeyDown={(event) => {
              // Two tabs: either arrow key activates the other one.
              if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
                setTab(t.key === "audit" ? "settings" : "audit");
              }
            }}
            style={tabStyle(tab === t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "audit" ? (
        <div
          role="tabpanel"
          id="panel-audit"
          aria-labelledby="tab-audit"
          data-testid="audit-panel"
          style={{ display: "grid", gap: 12 }}
        >
          <div
            role="group"
            aria-label="Audit filters"
            style={{ display: "flex", flexWrap: "wrap", gap: 16 }}
          >
            <FilterSelect
              id="audit-filter-event-type"
              label="Event type"
              value={filters.eventType}
              onChange={setFilter("eventType")}
            >
              <option value="">All types</option>
              {AUDIT_EVENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {eventTypeLabel(t)}
                </option>
              ))}
            </FilterSelect>
            <FilterSelect
              id="audit-filter-severity"
              label="Severity"
              value={filters.severity}
              onChange={setFilter("severity")}
            >
              <option value="">All severities</option>
              {AUDIT_SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {severityToken(s).label}
                </option>
              ))}
            </FilterSelect>
            <FilterSelect
              id="audit-filter-actor"
              label="Actor"
              value={filters.actor}
              onChange={setFilter("actor")}
            >
              <option value="">All actors</option>
              {actors.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </FilterSelect>
          </div>

          <DataTable
            ariaLabel="Audit log"
            columns={COLUMNS}
            rows={rows}
            getRowSeverity={(row) =>
              // Only paint an accent above "normal": a routine audit row stays
              // un-accented rather than a misleading green stripe.
              row._severity === "normal" ? null : row._severity
            }
            onRowClick={(row) => setSelected(row)}
            getRowLabel={(row) => `Open audit event: ${row.action}`}
            emptyMessage="No audit events recorded"
          />
        </div>
      ) : (
        <div
          role="tabpanel"
          id="panel-settings"
          aria-labelledby="tab-settings"
          data-testid="settings-panel"
          style={{ display: "grid", gap: 12 }}
        >
          <div>
            <h2 style={{ margin: "0 0 4px", fontSize: 16 }}>Alert thresholds</h2>
            <p style={{ margin: 0, fontSize: 13, color: theme.textMuted }}>
              Current value and agency default are always shown together. Any
              value that differs from the agency default is explicitly marked
              Overridden.
            </p>
          </div>
          {thresholdList.length === 0 ? (
            <div
              data-testid="settings-empty"
              style={{
                padding: "16px 12px",
                border: `1px solid ${theme.border}`,
                background: theme.surface,
                color: theme.textMuted,
                fontSize: 14,
              }}
            >
              No thresholds configured
            </div>
          ) : (
            thresholdList.map((t) => {
              // Effective client value: an explicit edit (null = reset) wins
              // over the configured clientValue; the model resolves the rest.
              const clientValue = Object.prototype.hasOwnProperty.call(
                edits,
                t.key,
              )
                ? edits[t.key]
                : t.clientValue;
              const resolved = resolveThreshold(clientValue, t.agencyDefault);
              return (
                <ThresholdRow
                  key={t.key}
                  threshold={t}
                  resolved={resolved}
                  onApply={applyThreshold}
                  onReset={resetThreshold}
                />
              );
            })
          )}
        </div>
      )}

      <DetailDrawer
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected ? selected.action : ""}
        type="audit"
      >
        {selected ? (
          <div data-testid="audit-detail" style={{ display: "grid", gap: 16 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <SeverityBadge
                level={selected._severity}
                label={severityToken(selected._severity).label}
              />
              <span
                style={{
                  marginLeft: "auto",
                  fontFamily: theme.fontMono,
                  fontSize: 12,
                  color: theme.textMuted,
                }}
              >
                {selected.when}
              </span>
            </div>
            <DrawerField label="Actor">{selected.actor}</DrawerField>
            <DrawerField label="Event type">{selected.action}</DrawerField>
            <DrawerField label="Target">{selected.target}</DrawerField>
            <DrawerField label="Timestamp">
              {formatAbsoluteTime(selected._entry.timestamp)}
            </DrawerField>
          </div>
        ) : null}
      </DetailDrawer>
    </div>
  );
}

AuditSettingsPage.propTypes = {
  /**
   * Audit trail entries; { id, actor, clientId, clientName, eventType,
   * severity, timestamp }. Typed as a plain array (not arrayOf(shape))
   * because the page is intentionally fail-safe for null / malformed rows,
   * and a strict element type would warn for those supported inputs.
   */
  entries: PropTypes.array,
  /** Alert thresholds; { key, label, unit, agencyDefault, clientValue }. */
  thresholds: PropTypes.array,
  /** Reference time for the relative "when" column. */
  now: PropTypes.number,
  /** Which tab opens first ("audit" | "settings") — set per entry route. */
  initialTab: PropTypes.oneOf(["audit", "settings"]),
};
